import type {
  DiagnosisModel,
  LifeStyleModel,
  PrescriptionModel,
  UserStatsModel,
  VitalsModel,
} from "../types/models";
import { getDate } from "../utils/date";
import { getFreshMetricValue, isStatsFresh } from "../utils/getVitalsStatus";
import { RangeBar } from "./RangeBar";

type PlanDetailsProps = {
  description?: string;
  startDate?: string;
  endDate?: string;
  lifestyle?: LifeStyleModel;
  vitals?: VitalsModel;
  prescription?: PrescriptionModel[];
  diagnoses?: DiagnosisModel[];
  currentStats?: UserStatsModel | null;
};

const vitalMeta: Array<{
  key: keyof VitalsModel;
  label: string;
  unit: string;
}> = [
  {
    key: "blood_systolic_pressure",
    label: "Systolic",
    unit: "mmHg",
  },
  {
    key: "blood_diastolic_pressure",
    label: "Diastolic",
    unit: "mmHg",
  },
  { key: "blood_glucose", label: "Glucose", unit: "mg/dL" },
  { key: "heart_rate", label: "Heart rate", unit: "bpm" },
];

export const PlanDetails = ({
  description,
  startDate,
  endDate,
  lifestyle,
  vitals,
  prescription = [],
  diagnoses = [],
  currentStats,
}: PlanDetailsProps) => {
  const hasLoggedStats = Boolean(currentStats && isStatsFresh(currentStats));

  const prescriptionTaken = (name: string) => {
    if (!hasLoggedStats || !currentStats?.prescription) return null;
    const match = currentStats.prescription.find((item) => item.name === name);
    return match ? Boolean(match.taken) : null;
  };

  return (
    <div className="stack gap-lg">
      {(startDate || endDate) && (
        <section className="panel">
          <h3>Dates</h3>
          <div className="detail-row">
            <span>Start</span>
            <span>{getDate(startDate)}</span>
          </div>
          <div className="detail-row">
            <span>End</span>
            <span>{getDate(endDate)}</span>
          </div>
        </section>
      )}

      {description ? (
        <section className="panel">
          <h3>Description</h3>
          <p>{description}</p>
        </section>
      ) : null}

      {diagnoses.length > 0 ? (
        <section className="panel">
          <h3>Diagnoses</h3>
          <ul className="plain-list">
            {diagnoses.map((item) => (
              <li key={item.id}>
                <strong>{item.icd_code}</strong> — {item.title}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {lifestyle ? (
        <section className="panel stack gap-md">
          <h3 className="plan-section-title">Lifestyle</h3>
          <RangeBar
            label="Sleep Hours"
            unit="hours"
            currentValue={getFreshMetricValue(currentStats, "sleep_hours")}
            goodRange={lifestyle.sleep_hours.good}
            warningRange={lifestyle.sleep_hours.warning}
          />
          <RangeBar
            label="Steps Count"
            unit="steps"
            currentValue={getFreshMetricValue(currentStats, "steps_count")}
            goodRange={lifestyle.steps_count.good}
            warningRange={lifestyle.steps_count.warning}
          />
        </section>
      ) : null}

      {vitals ? (
        <section className="panel stack gap-md">
          <h3 className="plan-section-title">Vital Stats</h3>
          {vitalMeta.map(({ key, label, unit }) => (
            <RangeBar
              key={key}
              label={label}
              unit={unit}
              currentValue={getFreshMetricValue(currentStats, key)}
              goodRange={vitals[key].good}
              warningRange={vitals[key].warning}
            />
          ))}
        </section>
      ) : null}

      {prescription.length > 0 ? (
        <section className="panel stack gap-md">
          <h3 className="plan-section-title">Prescription</h3>
          <ul className="prescription-status-list">
            {prescription.map((item, index) => {
              if (!item.name) return null;
              const taken = prescriptionTaken(item.name);
              return (
                <li key={`${item.name}-${index}`} className="prescription-status-item">
                  <div className="prescription-status-text">
                    <strong>{item.name}</strong>
                    <span>Dosage: {item.dosage}</span>
                    {taken !== null ? (
                      <span
                        className={`prescription-status-flag ${
                          taken ? "taken" : "not-taken"
                        }`}
                      >
                        Status: {taken ? "Taken" : "Not Taken"}
                      </span>
                    ) : (
                      <span className="muted">Status: Not logged yet</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
};
